import { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ReactPaginate from 'react-paginate';
import { useRouter } from 'next/router';

import { StateContext, DispatchContext } from '@/context/index';

import { getTagIdsBySlug, useDynamicPages } from '@/utils/api';
import { getPageNumber } from '@/utils/helper';
import { analytics, getPaginationDetail } from '@/utils/analytics';

import Text from '@/atoms/Text/Text';
import ContentGrid from '@/organisms/ContentGrid/ContentGrid';

import {
  ContentQueryOrder,
  CtfContentQueryOrder,
  PaginationDefaults,
} from '@/utils/constants';


function DynamicGrid({
  assets = [],
  entries = [],
  fields = null,
  columnLayout,
  isFromTwoColumnContent,
  isFromContentColumn,
  hasBackground,
}) {
  const router = useRouter();
  const { pages, queryTagIds, queryTagsKey, totalPages } = fields;
  const [newAssets, setNewAssets] = useState(assets);
  const [newEntries, setNewEntries] = useState(entries);
  const {
    dynamicGridParam: { [queryTagsKey]: ctxDynamicGridParam },
  } = useContext(StateContext);
  const dispatch = useContext(DispatchContext);
  const currentPage = getPageNumber(router.asPath) || 1;

  useEffect(() => {
    if (currentPage !== 1) {
      dispatch({
        type: 'SET_DYNAMIC_GRID_PARAMS',
        payload: {
          queryTagsKey,
          paginationNo: currentPage,
        },
      });
    }
  }, [dispatch, currentPage, queryTagsKey]);

  const { data, isLoading, isError } = useDynamicPages(
    ctxDynamicGridParam?.queryTagIds,
    ctxDynamicGridParam?.paginationNo,
    ctxDynamicGridParam?.paginationLimit,
    assets,
    entries,
    router.locale,
    ctxDynamicGridParam?.offset,
    'in',
    CtfContentQueryOrder.CREATE_DATE === fields.contentQueryOrder
      ? ContentQueryOrder.CREATE_DATE
      : ContentQueryOrder.PUBLISH_DATE
  );
  const [pageCount, setPageCount] = useState(
    Math.ceil(totalPages / fields.queryTagsLimit)
  );

  const getInjectedCards = (injectionCards, cards, pageNumber) => [
    ...cards.slice(0, Math.ceil(cards.length / 2)),
    injectionCards[pageNumber - 1],
    ...cards.slice(Math.ceil(cards.length / 2)),
  ];

  // function to turn pages into cards
  const pagesIntoCards = useCallback(
    (pages) =>
      pages?.map((i) => ({
        id: i.sys.id,
        primaryCTA: i.fields.slug,
        tags: i.fields.tags,
        title: i.fields.title,
        text: i.fields.previewText ?? null,
        cloudinaryAssets: i.fields.cloudinaryPreviewAssets ?? null,
        dynamicAsset: i.fields.previewAsset ?? null,
      })),
    []
  );

  // load data from SSG into the initial state
  const [newFields, setNewFields] = useState({
    ...fields,
    cards: fields.cards
      ? getInjectedCards(
          fields.cards,
          pagesIntoCards(pages),
          getPageNumber(router.asPath) || 1
        )
      : pagesIntoCards(pages),
  });

  //render data (CSR)
  useEffect(() => {
    if (!data) {
      return;
    }

    setNewAssets([...assets, ...data.filteredNewAssets]);
    setNewEntries([...entries, ...data.filteredNewEntries]);
    setPageCount(
      Math.ceil(data.resp.data.total / ctxDynamicGridParam.paginationLimit)
    );
    const pageNumber = ctxDynamicGridParam?.paginationNo || 1;
    let cards = pagesIntoCards(data.resp?.data?.items);
    let injectionCards = fields.cards;

    if (
      injectionCards &&
      injectionCards.length > 0 &&
      injectionCards.length <= pageNumber &&
      !ctxDynamicGridParam?.isFiltering
    ) {
      cards = getInjectedCards(injectionCards, cards, pageNumber);
    }

    setNewFields({
      ...fields,
      cards,
    });
  }, [
    assets,
    ctxDynamicGridParam?.isFiltering,
    ctxDynamicGridParam?.paginationLimit,
    ctxDynamicGridParam?.paginationNo,
    data,
    entries,
    fields,
    pagesIntoCards,
  ]);

  const handlePageChange = (e) => {
    const url = router.asPath;
    const urlPart = url.split('?');

    let urlPaths = urlPart[0].split('/');
    if (!isNaN(urlPaths[urlPaths.length - 1])) {
      urlPaths.pop();
    }

    analytics({
      window,
      document,
      detail: getPaginationDetail(e),
    });

    router.push(
      `${urlPaths.join('/')}/${e.selected + 1}${
        urlPart[1] ? '?' + urlPart[1] : ''
      }`,
      null,
      { shallow: true }
    );

    //if there is only 1 .aaco-content-grid, do animate scroll
    if(document.querySelectorAll('.aaco-content-grid').length === 1) {
      window.scrollTo({
        top: document.querySelector('.aaco-content-grid')?.offsetTop - 100 ?? 0,
        behavior: 'smooth',
      });
    }
  };

  let paginationLimit = fields.queryTagsLimit || PaginationDefaults.LIMIT;
  //set context's queryTagIds & paginationNo on first load (if has query params) / query params change
  useEffect(() => {
    const refetchData = async (url) => {
      const urlPart = url.split('?');
      const urlParams = new URLSearchParams(urlPart[[1]]);
      const filterParam = urlParams.get(queryTagsKey);
      const pageNumber = getPageNumber(url) || 1;
      let currentQueryTagIds = queryTagIds; // set queryTagIds default queryTagIds;

      if (filterParam && filterParam.length > 0) {
        // get tag ids
        const tagIdsResp = await getTagIdsBySlug(filterParam);
        const tagIdRespIds =
          tagIdsResp?.data?.items?.map((j) => j.sys.id) ?? [];
        const hasFilterApplied = tagIdRespIds?.length > 0
        // if has filter use filter tag, else , use default tag
        currentQueryTagIds = hasFilterApplied ? tagIdRespIds : currentQueryTagIds;
      }

      let offset = pageNumber > 1 ? (pageNumber - 1) * paginationLimit : 0;
      let limit = paginationLimit;
      // If we're injecting cards then calculate offset manually
      if (fields.cards && (!filterParam || !filterParam.length)) {
        limit--;
        offset =
          pageNumber > fields.cards.length
            ? fields.cards.length * (paginationLimit - 1) +
              (pageNumber - fields.cards.length - 1) * paginationLimit
            : (pageNumber - 1) * (paginationLimit - 1);
      }

      //set context's params data
      dispatch({
        type: 'SET_DYNAMIC_GRID_PARAMS',
        payload: {
          queryTagsKey,
          ...(queryTagIds && { queryTagIds: currentQueryTagIds }),
          ...(pageNumber && { paginationNo: pageNumber }),
          paginationLimit: limit,
          isFiltering: filterParam && filterParam.length > 0,
          offset: offset + (fields.queryTagsOffset ?? 0),
        },
      });
    };

    // refetch on first load (if has query params)
    if(router.asPath.includes('?')) {
      refetchData(router.asPath)
    }

    router.events.on('routeChangeStart', refetchData);
    return () => {
      router.events.off('routeChangeStart', refetchData);
    };
  }, [
    dispatch,
    fields.cards,
    fields.queryTagsLimit,
    queryTagIds,
    queryTagsKey,
    router.events,
  ]);

  if (!newFields.cards) return null;
  else if (isError)
    return (
      <div className="container">
        <Text tag="body">Something went wrong...</Text>
      </div>
    );

  return (
    <>
      <ContentGrid
        assets={newAssets}
        fields={newFields}
        entries={newEntries}
        className={`${isLoading ? 'opacity-50 pointer-events-none' : ''}`}
        columnLayout={columnLayout}
        isFromTwoColumnContent={isFromTwoColumnContent}
        isFromContentColumn={isFromContentColumn}
        hasBackground={hasBackground}
      />
      {pageCount > 1 && paginationLimit > 6 && (
        <div className="flex justify-center items-center mt-20 mb-20">
          <ReactPaginate
            forcePage={(ctxDynamicGridParam?.paginationNo ?? 1) - 1}
            pageCount={pageCount > 1 ? pageCount : 0}
            pageRangeDisplayed={5}
            nextLabel=""
            previousLabel=""
            renderOnZeroPageCount={null}
            className="aaco-pagination"
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </>
  );
}

DynamicGrid.propTypes = {
  assets: PropTypes.array,
  entries: PropTypes.array,
  fields: PropTypes.object,
};

export default DynamicGrid;
